import {useCallback, useEffect, useState} from 'react';
import {getIirEvaluations, upsertMyIirEvaluation, deleteMyIirEvaluation} from './iirEvaluationApi';

const EMPTY_SUMMARY = {averageScore: null, count: 0};

/**
 * Fetch + mutate hook for IIR evaluations on a report preview.
 * @param {string|null|undefined} reportId
 * @param {{enabled?: boolean}} [options]
 */
export default function useIirEvaluations(reportId, {enabled = true} = {}) {
    const [summary, setSummary] = useState(EMPTY_SUMMARY);
    const [evaluations, setEvaluations] = useState([]);
    const [mine, setMine] = useState(null);
    const [canEvaluate, setCanEvaluate] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const applyPayload = useCallback((data) => {
        if (!data) {
            setSummary(EMPTY_SUMMARY);
            setEvaluations([]);
            setMine(null);
            setCanEvaluate(false);
            return;
        }
        setSummary(data.summary || EMPTY_SUMMARY);
        setEvaluations(Array.isArray(data.evaluations) ? data.evaluations : []);
        setMine(data.mine ?? null);
        setCanEvaluate(Boolean(data.canEvaluate));
    }, []);

    const reload = useCallback(async () => {
        if (!reportId || !enabled) {
            applyPayload(null);
            setLoading(false);
            setError(null);
            return null;
        }
        setLoading(true);
        setError(null);
        try {
            const data = await getIirEvaluations(reportId);
            applyPayload(data);
            return data;
        } catch (err) {
            setError(err);
            applyPayload(null);
            return null;
        } finally {
            setLoading(false);
        }
    }, [reportId, enabled, applyPayload]);

    useEffect(() => {
        reload();
    }, [reload]);

    const saveMine = useCallback(async ({score, comment}) => {
        if (!reportId) throw new Error('reportId is required');
        setSaving(true);
        setError(null);
        try {
            const data = await upsertMyIirEvaluation(reportId, {score, comment});
            // Prefer full response if API returns the list payload; otherwise reload.
            if (data?.summary != null || Array.isArray(data?.evaluations)) {
                applyPayload(data);
            } else {
                await reload();
            }
            return data;
        } catch (err) {
            setError(err);
            throw err;
        } finally {
            setSaving(false);
        }
    }, [reportId, applyPayload, reload]);

    const deleteMine = useCallback(async () => {
        if (!reportId) throw new Error('reportId is required');
        setSaving(true);
        setError(null);
        try {
            const data = await deleteMyIirEvaluation(reportId);
            if (data?.summary != null || Array.isArray(data?.evaluations)) {
                applyPayload(data);
            } else {
                await reload();
            }
            return data;
        } catch (err) {
            setError(err);
            throw err;
        } finally {
            setSaving(false);
        }
    }, [reportId, applyPayload, reload]);

    return {
        summary,
        evaluations,
        mine,
        canEvaluate,
        loading,
        saving,
        error,
        reload,
        saveMine,
        deleteMine,
    };
}
